import { KAMCO_CONFIG } from 'constants/Kamco';
import { useMapImages } from 'hooks/ol/useMapImages';
import { useMapLayerManagement } from 'hooks/ol/useMapLayerManagement';
import { useOpenLayers } from 'hooks/ol/useOpenLayers';
import { useWMTS } from 'hooks/ol/useWMTS';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SystemStore } from 'stores/System';
import { TResultFeatureCollection } from 'types/TResult';
import { integerToDecimal } from 'utils/Utils';
import { LAYER_FACTORY } from 'utils/WebGLLayerFactory';
import Information from '../changeDetection/information/Information';
import Swipe from '../changeDetection/swipe/Swipe';
import { DetailChildProps } from '../Detail.type';
import CustomResultContainer from '../task/control/custom/container/CustomResultContainer';
import ResultControlItem from '../task/control/item/ResultControlItem';
import ResultControl from '../task/control/ResultControl';
import DetectionResultContainer from '../task/detectionResult/container/DetectionResultContainer';

const DetailPanel = ({ isChangeDetection, swipeRef, result, customLayerInfos, layerType }: DetailChildProps) => {
    const [swipe, setSwipevalue] = useState<number>(50);
    const [isShowResult, setShowResult] = useState(true);
    const [isShowKamcoCustom, setShowKamcoCustom] = useState(true);
    const [resultOpacity, setResultOpacity] = useState(100);
    const [kamcoCustomOpacity, setKamcoCustomOpacity] = useState(100);
    const [isShowImage, setShowImage] = useState(true);
    const [imageOpacity, setImageOpacity] = useState(100);
    const [isShowCustom, setShowCustom] = useState(false);
    const [isResultAllChecked, setIsResultAllChecked] = useState(true);
    const [isCustomAllChecked, setIsCustomAllChecked] = useState(false);
    const [isKamcoCustomAllChecked, setIsKamcoCustomAllChecked] = useState(true);
    const [changeChildChecked, setChangeChildChecked] = useState(false);
    const [changeKamcoCustomChildChecked, setChangeKamcoCustomChildChecked] = useState(false);
    const [changeCustomChildChecked, setChangeCustomChildChecked] = useState(false);
    const [resultOpacityDisabled, setResultOpacityDisabled] = useState(false);
    const [imageOpacityDisabled, setImageOpacityDisabled] = useState(false);
    const [kamcoCustomOpacityDisabled, setKamcoCustomOpacityDisabled] = useState(false);
    const [kamcoCustomFeatureCollections, setKamcoCustomFeatureCollections] = useState<TResultFeatureCollection[]>([]);
    const { isKamcoDemo } = SystemStore();
    const { addWMTS } = useWMTS();
    
    // MEMO: Props drilling 방지 필요한 훅들 직접 사용함 lucy
    const { updateLayerOpacity, updateLayerVisible, updateKamcoCustomLayerOpacity, updateKamcoCustomLayerVisible, updateLayerVisibleByName, findCustomLayer, findResultLayer } = useMapLayerManagement();
    const { updateBaseImageOpacity, updateBaseImageVisible, drawCompareImage } = useMapImages();
    const { drawKamcoCustomResults, rerender, makeLayerName } = useOpenLayers();

    const { t } = useTranslation();

    useEffect(() => {
        if (isKamcoDemo) {
            addWMTS(KAMCO_CONFIG.TARGET_WMTS_URL, KAMCO_CONFIG.TARGET_WMTS_LAYER, "target");
            getKamcoCustomLayer();
        } else {
            drawCompareImage(result.request.requestImageDataList[1].imageTileUrl);
        }
    }, [])

    // 캠코 창원시 6차 데이터
    const getKamcoCustomLayer = async () => {
        try {
            const response = await fetch(KAMCO_CONFIG.KAMCO_CUSTOM_LAYER_URL).then((r) => r.json());
            const featureCollections = response.featureCollections;

            setKamcoCustomFeatureCollections(featureCollections);
            await drawKamcoCustomResults(featureCollections);
        } catch (error) {
            console.error('Failed to load KAMCO custom layer:', error);
        }
    }

    const hanldeChangSwipeValue = (value: number) => {
        if (swipeRef) swipeRef.current = value;
        setSwipevalue(value);
        rerender();
    };

    //타일 이미지 Opacity 세팅
    const onChangeImageOpacity = (opacity: number) => {
        setImageOpacity(opacity);
        updateBaseImageOpacity(integerToDecimal(opacity, 2));
    }

    //타일 이미지 Visible 세팅
    const onChangeImageVisible = (is: boolean) => {
        setShowImage(is);
        updateBaseImageVisible(is);
        setImageOpacityDisabled(!is);
    }

    //레이어 전체 Opacity 세팅
    const onChangeResultOpacity = (opacity: number) => {
        setResultOpacity(opacity);
        updateLayerOpacity(integerToDecimal(opacity, 2));
    }

    // 캠코 6차 데이터 Opacity 세팅
    const onChangeKamcoCustomOpacity = (opacity: number) => {
        setKamcoCustomOpacity(opacity);
        updateKamcoCustomLayerOpacity(integerToDecimal(opacity, 2));
    }

    //레이어 전체 Visible 세팅 
    const onChangeLayerVisible = (is: boolean, isCustom: boolean) => {
        isCustom ? setChangeCustomChildChecked(true) : setChangeChildChecked(true);
        isCustom ? setShowCustom(is) : setShowResult(is);
        isCustom ? setIsCustomAllChecked(is) : setIsResultAllChecked(is);
        updateLayerVisible(is, isCustom);
        !isCustom && setResultOpacityDisabled(!is);
    }

    // 캠코 6차 데이터 Visible 세팅
    const onChangeKamcoCustomVisible = (is: boolean) => {
        setChangeKamcoCustomChildChecked(true);
        setShowKamcoCustom(is);
        setIsKamcoCustomAllChecked(is);
        updateKamcoCustomLayerVisible(is);
        setKamcoCustomOpacityDisabled(!is);
    }

    //layer name별 Visible 세팅
    const onChangeLayerVisibleByName = (layerName: string, is: boolean) => {
        setChangeChildChecked(false);
        const isCustom = layerName.startsWith(LAYER_FACTORY.CUSTOM);
        updateLayerVisibleByName(layerName, is);
        const layers = isCustom ? findCustomLayer() : findResultLayer();
        const visibleLayerLength = layers?.filter(i => i.getVisible()).length;
        const isAllVisible = layers?.length === visibleLayerLength;
        !isCustom && setResultOpacityDisabled(visibleLayerLength === 0);
        isCustom ? setShowCustom(isAllVisible) : setShowResult(isAllVisible);
        isAllVisible && (isCustom ? setIsCustomAllChecked(isAllVisible) : setIsResultAllChecked(isAllVisible));
    }

    const initCustomResult = useMemo(() => {
        if (!customLayerInfos || customLayerInfos.length === 0) return;
        return (
            <ResultControl title={t('panel.change_detection.geographic_data')} checked={isShowCustom} onChangeCheck={(e) => onChangeLayerVisible(e, true)}>
                <CustomResultContainer>
                    {customLayerInfos.map((layer, index) => {
                        return (<div key={index}><ResultControlItem title={layer.name} layerName={makeLayerName(LAYER_FACTORY.CUSTOM, index)} parentsChecked={isCustomAllChecked} changeVisible={changeCustomChildChecked} featureCount={layer.featureCount} onChangeCheck={onChangeLayerVisibleByName} layerType={LAYER_FACTORY.CUSTOM}></ResultControlItem></div>)
                    })}
                </CustomResultContainer>
            </ResultControl>

        )
    }, [customLayerInfos, isShowCustom, isCustomAllChecked, changeCustomChildChecked])

    const initResult = useMemo(() => {
        const totalLength = result.featureCollections.reduce((acc, fc) => acc + fc.features.length, 0);
        return (
            <ResultControl title={isKamcoDemo ? '4차 데이터' : t('panel.detail.detection_result')} checked={isShowResult} onChangeCheck={(e) => onChangeLayerVisible(e, false)} opacity={resultOpacity} onChangeOpacity={onChangeResultOpacity} opacityDisabled={resultOpacityDisabled}>
                <DetectionResultContainer total={totalLength.toLocaleString('ko-KR')}>
                    {result.featureCollections && result.featureCollections.map((layer, index) => {
                        return (<div key={index}><ResultControlItem title={layer.class} layerName={makeLayerName(layerType, index)} parentsChecked={isResultAllChecked} changeVisible={changeChildChecked} featureCount={layer.features.length} onChangeCheck={onChangeLayerVisibleByName} layerType={layerType}></ResultControlItem></div>)
                    })}
                </DetectionResultContainer>
            </ResultControl>

        )
    }, [result, resultOpacity, isShowResult, isResultAllChecked, resultOpacityDisabled])

    const initKamcoCustomLayer = useMemo(() => {
        if (kamcoCustomFeatureCollections.length === 0) return null;
        const totalLength = kamcoCustomFeatureCollections.reduce((acc, fc) => acc + fc.features.length, 0);
        return (
            <ResultControl title="6차 데이터" checked={isShowKamcoCustom} onChangeCheck={(e) => onChangeKamcoCustomVisible(e)} opacity={kamcoCustomOpacity} onChangeOpacity={onChangeKamcoCustomOpacity} opacityDisabled={kamcoCustomOpacityDisabled}>
                <DetectionResultContainer total={totalLength.toLocaleString('ko-KR')}>
                    {kamcoCustomFeatureCollections && kamcoCustomFeatureCollections.map((layer, index) => {
                        return (<div key={index}><ResultControlItem title={layer.class} layerName={makeLayerName(LAYER_FACTORY.KAMCO_CUSTOM, index)} parentsChecked={isKamcoCustomAllChecked} changeVisible={changeKamcoCustomChildChecked} featureCount={layer.features.length} onChangeCheck={onChangeLayerVisibleByName} layerType={LAYER_FACTORY.KAMCO_CUSTOM}></ResultControlItem></div>)
                    })}
                </DetectionResultContainer>
            </ResultControl>
        )
    }, [kamcoCustomFeatureCollections, isShowKamcoCustom, kamcoCustomOpacity, kamcoCustomOpacityDisabled, isKamcoCustomAllChecked, changeKamcoCustomChildChecked])

    return (
        <>
            <Information request={result.request} />
            {initResult}
            {initCustomResult}
            {isKamcoDemo && initKamcoCustomLayer}
            <ResultControl title="Data Images" checked={isShowImage} isFolder={false} opacity={imageOpacity} onChangeCheck={onChangeImageVisible} onChangeOpacity={onChangeImageOpacity} opacityDisabled={imageOpacityDisabled} />
            {isChangeDetection && createPortal(<Swipe dateStr={[result.request.requestImageDataList[0].imageCreatedDate, result.request.requestImageDataList[1].imageCreatedDate]} value={swipe} onChange={hanldeChangSwipeValue} />, document.getElementById('map-slider-root')!)}
        </>
    );
};

export default DetailPanel;
